import "./BirthdaysCard.css";

import { useEffect, useMemo, useState } from "react";
import { BsCake2 } from "react-icons/bs";
import { Card } from "primereact/card";

import { getBirthdays } from "../../services/wisdomServices/userService";
import { Can, PERMISSIONS } from "../../security";

const BirthdaysCard = () => {
  const [birthdays, setBirthdays] = useState([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [birthdaysError, setBirthdaysError] = useState(false);

  useEffect(() => {
    loadBirthdays();
  }, []);

  const loadBirthdays = async () => {
    try {
      setLoadingBirthdays(true);
      setBirthdaysError(false);

      const response = await getBirthdays();

      setBirthdays(response.data);
    } catch (error) {
      console.error("Error loading birthdays:", error);
      setBirthdaysError(true);
      setBirthdays([]);
    } finally {
      setLoadingBirthdays(false);
    }
  };

  const getBirthdayParts = (birthday) => {
    if (!birthday) {
      return {
        month: 0,
        day: 0,
      };
    }

    const [, month, day] = birthday.split("-").map(Number);

    return {
      month,
      day,
    };
  };

  const isBirthdayToday = (birthday) => {
    const { month, day } = getBirthdayParts(birthday);
    const today = new Date();

    return (
      month === today.getMonth() + 1 &&
      day === today.getDate()
    );
  };

  const formatBirthday = (birthday) => {
    const { month, day } = getBirthdayParts(birthday);

    if (!month || !day) {
      return "Date unavailable";
    }

    const birthdayDate = new Date(2000, month - 1, day);

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
    }).format(birthdayDate);
  };

  const getBirthdayDay = (birthday) => {
    const { day } = getBirthdayParts(birthday);

    return String(day).padStart(2, "0");
  };

  const getBirthdayMonth = (birthday) => {
    const { month } = getBirthdayParts(birthday);

    if (!month) {
      return "";
    }

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
    })
      .format(new Date(2000, month - 1, 1))
      .toUpperCase();
  };

  const getInitials = (fullName = "") => {
    const names = fullName.trim().split(/\s+/).filter(Boolean);

    if (!names.length) {
      return "?";
    }

    if (names.length === 1) {
      return names[0].slice(0, 2).toUpperCase();
    }

    return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
  };

  const sortedBirthdays = useMemo(() => {
    return [...birthdays].sort((firstBirthday, secondBirthday) => {
      const firstIsToday = isBirthdayToday(firstBirthday.birthday);
      const secondIsToday = isBirthdayToday(secondBirthday.birthday);

      if (firstIsToday !== secondIsToday) {
        return firstIsToday ? -1 : 1;
      }

      const firstDate = getBirthdayParts(firstBirthday.birthday);
      const secondDate = getBirthdayParts(secondBirthday.birthday);

      return firstDate.day - secondDate.day;
    });
  }, [birthdays]);

  const todayBirthdaysCount = useMemo(() => {
    return birthdays.filter((birthday) =>
      isBirthdayToday(birthday.birthday)
    ).length;
  }, [birthdays]);

  const titleBodyTemplate = () => {
    return (
      <div className="birthdays-card-title">
        <div className="birthdays-card-title-content">
          <h4>
            <BsCake2 />
            Birthday Reminders

          {todayBirthdaysCount > 0 && (
            <span className="birthdays-today-header">
              🎉
              {todayBirthdaysCount === 1
                ? "There is 1 birthday today!"
                : `There are ${todayBirthdaysCount} birthdays today!`}
            </span>
          )}
          </h4>
        </div>
      </div>
    );
  };

  const renderLoading = () => {
    return (
      <div className="birthdays-loading">
        {[1, 2, 3].map((item) => (
          <div className="birthdays-loading-item" key={item}>
            <span className="birthdays-loading-avatar" />

            <div className="birthdays-loading-content">
              <span />
              <span />
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card
      title={titleBodyTemplate}
      className="birthdays-card"
    >
      {loadingBirthdays ? (
        renderLoading()
      ) : birthdaysError ? (
        <div className="birthdays-card-state birthdays-card-state-error">
          <BsCake2 />
          <span>Unable to load birthdays.</span>
          <Can permission={PERMISSIONS.usersBirthdays_get}>
            <button type="button" onClick={loadBirthdays}>
              Try again
            </button>
          </Can>
        </div>
      ) : birthdays.length === 0 ? (
        <div className="birthdays-card-state">
          <BsCake2 />
          <span>No birthdays this month.</span>
        </div>
      ) : (
        <div className="birthdays-card-content">

          <ul className="birthdays-list">
            {sortedBirthdays.map((birthday) => {
              const birthdayIsToday = isBirthdayToday(
                birthday.birthday
              );

              return (
                <li
                  key={birthday.id}
                  className={`birthday-item ${
                    birthdayIsToday
                      ? "birthday-item-today"
                      : ""
                  }`}
                >
                  <div className="birthday-avatar">
                    {getInitials(birthday.fullName)}
                  </div>

                  <div className="birthday-info">
                    <strong>{birthday.fullName}</strong>

                    <span>
                      {birthdayIsToday
                        ? "Celebrating today!"
                        : formatBirthday(birthday.birthday)}
                    </span>
                  </div>

                  {birthdayIsToday ? (
                    <span className="birthday-today-badge">
                      Today
                    </span>
                  ) : (
                    <div className="birthday-date">
                      <strong>
                        {getBirthdayDay(birthday.birthday)}
                      </strong>

                      <span>
                        {getBirthdayMonth(birthday.birthday)}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default BirthdaysCard;